import api from '../utils/api'

/**
 * The hourly invoice EDIT endpoints on CM BE.
 *
 * THESE CALL CM BE, NEVER SCALE DIRECTLY.
 * `src/utils/scaleApi.js` is dead code with a stale premise: it forwards the
 * portal's token to scale-be, which stopped working when case managers moved
 * off Supabase Auth onto CM-minted HS256 tokens — scale-be's authMiddleware
 * only validates Supabase JWTs, so every such call 401s. CM BE mints the Scale
 * credential itself, so the browser sends nothing Scale-specific: just the
 * normal `Authorization: Bearer <cm token>` that `utils/api` already attaches.
 */

/* An update VOIDS the live Stripe invoice and reissues it, per party, and then
 * emails each one. CM BE allows 300s for that round trip, so the browser has to
 * wait longer than the server does — a client-side abort at 20s or 60s would
 * leave a half-applied edit with no way to see which invoices actually went
 * out. The shared `api` instance sets no timeout, so this is opt-in per call. */
const UPDATE_TIMEOUT_MS = 330000

/* The read is cheap. It only has to outlast a cold Render dyno. */
const CONTEXT_TIMEOUT_MS = 60000

/**
 * Everything the wizard needs, in one call: the nested case (plaintiff /
 * defendant / additionalParties), the getAccountData-shaped mediator, and the
 * hourly_invoice_payment rows with their invoice_history.
 *
 * @returns the `data` object, or null when the response carried none.
 */
export const getHourlyEditContext = async (caseId, { timeout } = {}) => {
    const res = await api.get(`/cases/${caseId}/hourly-billing/edit-context`, {
        timeout: timeout ?? CONTEXT_TIMEOUT_MS,
    })
    return res?.data?.data || null
}

/**
 * Submit the edit.
 *
 * Scale answers 200 even on PARTIAL failure, on purpose, so a half-applied
 * "both" cannot read as a clean success — which is why this returns the whole
 * body (`{ success, message, results }`) rather than throwing on `success:
 * false`. The caller must render `results[]` per invoice.
 *
 * A retry must resend ONLY the failed ids: every submit voids and reissues, so
 * resending a success voids the invoice just created and emails the party a
 * second time.
 */
export const updateHourlyInvoices = async (caseId, payload) => {
    const res = await api.put(`/cases/${caseId}/hourly-billing/update`, payload, {
        timeout: UPDATE_TIMEOUT_MS,
    })
    return res?.data || null
}

/**
 * The fixed-invoice proxy, unchanged on the wire. Listed here only so the two
 * money routes are found together; the fixed editor still calls it inline.
 */
export const FIXED_UPDATE_PROXY_PATH = '/billing/update-fixed-proxy'
